/**
 * Modem pin (#252): optionally run a test on ONE specific modem at the chosen
 * location — a persistent-attach or PLMN-hunt modem, typically. Default is
 * "any modem" (the allocator picks). Threaded to the backend via
 * `locationRequirements[0].deviceId / modemImei`.
 */
import { useEffect, useState } from 'react';
import { Label } from '@/components/ui/Label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/Select';
import { Badge } from '@/components/ui/Badge';
import { edgeDeviceService } from '@/services/edgeDeviceService';

const ANY = '__any__';

interface Option {
  deviceId: string;
  deviceName: string;
  imei: string;
  model: string;
  mode: string | null;
  online: boolean;
}

export function ModemPinPicker({
  locationId, pinDeviceId, pinModemImei, onChange,
}: {
  locationId: string;
  pinDeviceId?: string;
  pinModemImei?: string;
  onChange: (pin: { deviceId: string; modemImei: string } | null) => void;
}) {
  const [options, setOptions] = useState<Option[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const devices = await edgeDeviceService.getDevices();
        const here = devices.filter((d: any) => d.locationId === locationId);
        const perDevice = await Promise.all(here.map(async (d: any) => {
          const modems = await edgeDeviceService.getModems(d.id).catch(() => []);
          return modems
            .filter((m: any) => m.imei && m.disabled !== true && m.backhaul !== true)
            .map((m: any) => ({
              deviceId: d.id,
              deviceName: d.name || d.id,
              imei: m.imei,
              model: m.modemModelName || m.model || 'modem',
              mode: m.connectivityMode || null,
              online: m.status === 'online',
            }));
        }));
        if (!cancelled) setOptions(perDevice.flat());
      } catch {
        if (!cancelled) setOptions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [locationId]);

  const value = pinModemImei ? `${pinDeviceId || ''}|${pinModemImei}` : ANY;

  return (
    <div className="mt-2 space-y-1">
      <Label className="text-xs text-muted-foreground">Pin to modem (optional)</Label>
      <Select
        value={value}
        onValueChange={(v) => {
          if (v === ANY) { onChange(null); return; }
          const [deviceId, modemImei] = v.split('|');
          onChange({ deviceId, modemImei });
        }}
        disabled={loading}
      >
        <SelectTrigger><SelectValue placeholder={loading ? 'Loading modems…' : 'Any modem (allocator picks)'} /></SelectTrigger>
        <SelectContent>
          <SelectItem value={ANY}>Any modem (allocator picks)</SelectItem>
          {options.map((o) => (
            <SelectItem key={`${o.deviceId}|${o.imei}`} value={`${o.deviceId}|${o.imei}`}>
              <span className="inline-flex items-center gap-2">
                <span>{o.deviceName} · {o.model} · …{o.imei.slice(-6)}</span>
                {o.mode && <Badge variant="outline" className="text-[10px]">{o.mode === 'hunt' ? 'PLMN hunt' : 'persistent'}</Badge>}
                {!o.online && <Badge variant="secondary" className="text-[10px]">offline</Badge>}
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {pinModemImei && (
        <p className="text-[11px] text-muted-foreground">
          The run waits for this exact modem. Forced network type / PLMN and eSIM switches are refused on a
          persistent or hunt modem.
        </p>
      )}
    </div>
  );
}
